import { ReactNode, useCallback } from "react";
import * as React from "react";
import {
  Controller,
  DeepMap,
  FieldError,
  FieldErrors,
  FieldValues,
  UseControllerProps
} from "react-hook-form";

import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { get } from "lodash";

import { DEFAULT_LABEL_CONFIG } from "app/themes/components/input";

export type RadioGroupOption = {
  value: string | number;
  label: string;
  disabled?: boolean;
  tooltip?: ReactNode;
};

type RadioGroupFormFieldProps = {
  options: RadioGroupOption[];
  label: string;
  disabled?: boolean;
  required?: boolean;
  row?: boolean;
  sx?: SxProps;
  optionSx?: SxProps;
  afterChange?: (value: string) => void;
};

export type RhfRadioGroupFormFieldProps<T extends FieldValues> =
  RadioGroupFormFieldProps &
    UseControllerProps<T> & { errors?: FieldErrors<T> };

export default function RadioGroupFormField<T extends FieldValues>({
  name,
  label,
  disabled = false,
  control,
  row = false,
  options,
  required = false,
  errors,
  afterChange,
  optionSx,
  sx
}: RhfRadioGroupFormFieldProps<T>) {
  const validate = useCallback(
    (value: any) =>
      !required ||
      (required && value !== null && value !== undefined && value !== "")
        ? true
        : `${label || "Selection"} is required`,
    [required, label]
  );
  const errorMessage =
    errors &&
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;
  return (
    <FormControl
      disabled={disabled}
      required={required}
      fullWidth
      error={Boolean(errorMessage)}
      sx={sx}
    >
      {Boolean(label) && (
        <FormLabel sx={DEFAULT_LABEL_CONFIG} id={name}>
          {label}
        </FormLabel>
      )}
      <Controller
        rules={{ validate }}
        control={control}
        name={name}
        render={({ field }) => (
          <RadioGroup
            row={row}
            {...field}
            sx={optionSx}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              field.onChange(event);
              if (afterChange) {
                afterChange(event.target.value);
              }
            }}
          >
            {options.map((option) => (
              <Tooltip key={option.value} title={option.tooltip}>
                <FormControlLabel
                  label={<Typography sx={optionSx}>{option.label}</Typography>}
                  value={option.value}
                  disabled={option.disabled}
                  control={<Radio />}
                />
              </Tooltip>
            ))}
          </RadioGroup>
        )}
      />
      {Boolean(errorMessage) && (
        <FormHelperText error={Boolean(errorMessage)}>
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
}
